import { useCallback, useEffect, useState } from 'react';
import { apiBase, withApiKey } from '../apiAuth';

type ManageTemplatesModalProps = {
  open: boolean;
  onClose: () => void;
  onStatus: (message: string) => void;
};

type CatalogItem = {
  uniqueId: string;
  title?: string;
  preview?: string;
  json?: string;
};

export function ManageTemplatesModal({ open, onClose, onStatus }: ManageTemplatesModalProps) {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(withApiKey(`${apiBase()}/api/get-templates?page=1`));
      const data = (await res.json().catch(() => ({}))) as {
        items?: CatalogItem[];
        error?: string;
      };
      if (!res.ok) {
        setError(data.error || `Failed to load (${res.status})`);
        setItems([]);
        return;
      }
      const userOnly = (data.items || []).filter((it) =>
        String(it.uniqueId || '').startsWith('user-'),
      );
      setItems(userOnly);
    } catch (err) {
      console.error(err);
      setError('Could not reach /api/get-templates');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const handleDelete = async (item: CatalogItem) => {
    const password = window.prompt(
      `Delete template “${item.title || item.uniqueId}”?\n\nEnter delete password:`,
    );
    if (password === null) return;
    if (!password.trim()) {
      onStatus('Delete cancelled — password required');
      return;
    }
    if (
      !window.confirm(
        `Permanently delete ${item.uniqueId}? Related R2 assets (preview, source, meta) will be removed.`,
      )
    ) {
      return;
    }

    setDeletingId(item.uniqueId);
    setError(null);
    try {
      const res = await fetch(withApiKey(`${apiBase()}/api/templates/${encodeURIComponent(item.uniqueId)}`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Delete-Password': password,
        },
        body: JSON.stringify({ password }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        message?: string;
        deleted?: string[];
      };
      if (!res.ok) {
        const msg = data.message || data.error || `Delete failed (${res.status})`;
        setError(msg);
        onStatus(`Delete failed: ${msg}`);
        return;
      }
      onStatus(`Deleted ${item.uniqueId} (${data.deleted?.length ?? 0} keys)`);
      setItems((prev) => prev.filter((x) => x.uniqueId !== item.uniqueId));
    } catch (err) {
      console.error(err);
      setError('Delete request failed — is the API running?');
      onStatus('Delete request failed');
    } finally {
      setDeletingId(null);
    }
  };

  if (!open) return null;

  const titleId = 'manage-templates-title';

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal modal--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 id={titleId} className="modal__title">
            Manage templates
          </h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p className="modal__lead">
          User-saved templates only (ids starting with <code>user-</code>). Deletion requires the
          Worker <code>TEMPLATE_DELETE_PASSWORD</code>. Docs pack and built-ins cannot be removed
          here.
        </p>

        {loading && (
          <div className="modal__status" role="status">
            Loading user templates…
          </div>
        )}
        {error && (
          <div className="modal__error" role="alert">
            {error}
          </div>
        )}

        {!loading && items.length === 0 && !error && (
          <div className="modal__status">No user templates yet. Use Save as template or Image to template.</div>
        )}

        <ul className="tmpl-manage-list">
          {items.map((item) => (
            <li key={item.uniqueId} className="tmpl-manage-list__row">
              <div className="tmpl-manage-list__info">
                <div className="tmpl-manage-list__title">{item.title || 'Untitled'}</div>
                <div className="tmpl-manage-list__id">{item.uniqueId}</div>
              </div>
              <button
                type="button"
                className="btn btn--danger"
                disabled={deletingId === item.uniqueId}
                onClick={() => void handleDelete(item)}
              >
                {deletingId === item.uniqueId ? 'Deleting…' : 'Delete'}
              </button>
            </li>
          ))}
        </ul>

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost-dark" onClick={() => void load()} disabled={loading}>
            Refresh
          </button>
          <button type="button" className="btn btn--accent" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
